import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNow } from '../hooks/useNow';
import { referralStatusLabel } from '../lib/referralStatus';
import { getOpenOverdueTimer, OPEN_STATUS } from '../lib/referralTimeLimits';
import { ensureIncidentLookup, } from '../services/incidentLookup';
import { CLOSED_STATUS, incidentLevelLabel } from '../lib/incidentLevels';
const COLUMNS = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'referId', label: 'Case ID', sortable: true },
    { key: 'issueType', label: 'Incident type', sortable: true },
    { key: 'level', label: 'Level', sortable: false },
    { key: 'createdByName', label: 'Created By', sortable: true },
    { key: 'ownerName', label: 'Owner', sortable: true },
    { key: 'created', label: 'Created', sortable: true },
    { key: 'updated', label: 'Last Update', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
];
/** Refresh every second so the displayed minute advances as soon as it turns. */
const OVERDUE_TICK_MS = 1000;
function formatDate(iso) {
    if (!iso)
        return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '—';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}
function formatDateTime(iso) {
    if (!iso)
        return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '—';
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
function columnClassName(key) {
    switch (key) {
        case 'status':
            return 'col-status';
        case 'created':
            return 'col-date';
        case 'updated':
            return 'col-datetime';
        case 'createdByName':
            return 'col-created-by';
        case 'ownerName':
            return 'col-owner';
        case 'issueType':
            return 'col-issue-type';
        case 'level':
            return 'col-level';
        case 'referId':
            return 'col-case-id';
        default:
            return undefined;
    }
}
function StatusCell({ created, status, nowMs, statuses, }) {
    const overdue = getOpenOverdueTimer(created, status, undefined, nowMs);
    return (_jsxs("span", { className: "status-with-overdue", children: [_jsx("span", { className: `status-badge status-${status}`, children: referralStatusLabel(status, statuses) }), overdue && (_jsx("span", { className: "referral-overdue-mark", title: "Overdue", "aria-label": "Overdue", children: "!" }))] }));
}
function groupReferrals(referrals) {
    const groups = [];
    for (const row of referrals) {
        const last = groups[groups.length - 1];
        const parent = last?.[0];
        if (parent && parent.parentId == null && row.parentId === parent.id) {
            last.push(row);
        }
        else {
            groups.push([row]);
        }
    }
    return groups;
}
function SortControls({ sortColumn, sortDirection, onSort, }) {
    return (_jsx(_Fragment, { children: COLUMNS.filter((column) => column.sortable).map((column) => {
            const key = column.key;
            const active = sortColumn === key;
            return (_jsxs("button", { type: "button", className: `th-sort${active ? ' th-sort-active' : ''}`, onClick: () => onSort(key), children: [column.label, _jsx("span", { className: "th-sort-arrow", "aria-hidden": "true", children: active ? (sortDirection === 'asc' ? '▲' : '▼') : '↕' })] }, column.key));
        }) }));
}
function canOpenReferral(referral, lockOpenL2) {
    if (!lockOpenL2 || referral.level !== 'L2')
        return true;
    return referral.status === CLOSED_STATUS;
}
export function ReferralsTable({ referrals, loading, sortColumn, sortDirection, onSort, lockOpenL2 = false, }) {
    const [statuses, setStatuses] = useState([]);
    const needsLiveTimers = referrals.some((r) => r.status === OPEN_STATUS);
    const nowMs = useNow(OVERDUE_TICK_MS, needsLiveTimers && !loading);
    useEffect(() => {
        ensureIncidentLookup()
            .then((lookup) => setStatuses(lookup.statuses))
            .catch((err) => console.error('[incidentLookup] statuses failed', err));
    }, []);
    if (loading) {
        return _jsx("div", { className: "table-empty", children: "Loading L1s\u2026" });
    }
    if (referrals.length === 0) {
        return _jsx("div", { className: "table-empty", children: "No L1s found." });
    }
    const groups = groupReferrals(referrals);
    return (_jsxs("div", { className: "referrals-list", children: [_jsx("div", { className: "table-wrap referrals-table-desktop", children: _jsxs("table", { className: "referrals-table", children: [_jsx("thead", { children: _jsx("tr", { children: COLUMNS.map((column) => (_jsx("th", { className: columnClassName(column.key), children: column.sortable ? (_jsxs("button", { type: "button", className: `th-sort${sortColumn === column.key ? ' th-sort-active' : ''}`, onClick: () => onSort(column.key), children: [column.label, _jsx("span", { className: "th-sort-arrow", "aria-hidden": "true", children: sortColumn === column.key
                                                    ? sortDirection === 'asc'
                                                        ? '▲'
                                                        : '▼'
                                                    : '↕' })] })) : (_jsx("span", { className: "th-label", children: column.label })) }, column.key))) }) }), groups.map((group) => (_jsx("tbody", { className: `referral-group${group.length > 1 ? ' has-children' : ''}`, children: group.map((r) => (_jsxs("tr", { className: r.parentId != null ? 'is-child-row' : 'is-parent-row', children: [_jsxs("td", { className: "mono", children: [canOpenReferral(r, lockOpenL2) ? (_jsx(Link, { className: "referral-id-link", to: `/referrals/${r.id}`, children: r.id })) : (_jsx("span", { title: "L2 details are available after this incident is closed.", children: r.id })), r.parentId != null && (_jsxs("div", { className: "child-parent-hint", children: ["Parent L1 #", r.parentId] }))] }), _jsx("td", { className: "col-case-id mono", children: r.referId != null ? r.referId : _jsx("span", { className: "text-muted", children: "\u2014" }) }), _jsx("td", { className: "col-issue-type", children: r.issueType ? (_jsx("span", { title: r.issueType, children: r.issueType })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) }), _jsx("td", { className: "col-level", children: incidentLevelLabel(r.level) }), _jsx("td", { className: "col-created-by", children: r.createdByName ? (_jsx("span", { title: r.createdByEmail, children: r.createdByName })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) }), _jsx("td", { className: "col-owner", children: r.ownerName ? r.ownerName : _jsx("span", { className: "text-muted", children: "\u2014" }) }), _jsx("td", { className: "col-date", children: formatDate(r.created) }), _jsx("td", { className: "col-datetime", children: formatDateTime(r.updated) }), _jsx("td", { className: "col-status", children: _jsx(StatusCell, { created: r.created, status: r.status, nowMs: nowMs, statuses: statuses }) })] }, r.id))) }, group[0].id)))] }) }), _jsxs("div", { className: "referrals-cards", children: [_jsx("div", { className: "referrals-cards-sort", "aria-label": "Sort L1s", children: _jsx(SortControls, { sortColumn: sortColumn, sortDirection: sortDirection, onSort: onSort }) }), _jsx("ul", { className: "referrals-card-list", children: groups.map((group) => (_jsx("li", { className: `referral-card-group${group.length > 1 ? ' has-children' : ''}`, children: group.map((r) => (_jsxs("div", { className: `referral-card${r.parentId != null ? ' is-child-row' : ''}`, children: [_jsxs("div", { className: "referral-card-head", children: [canOpenReferral(r, lockOpenL2) ? (_jsxs(Link, { className: "referral-id-link mono", to: `/referrals/${r.id}`, children: ["#", r.id] })) : (_jsxs("span", { className: "mono", title: "L2 details are available after this incident is closed.", children: ["#", r.id] })), _jsx(StatusCell, { created: r.created, status: r.status, nowMs: nowMs, statuses: statuses })] }), _jsxs("dl", { className: "referral-card-meta", children: [_jsxs("div", { children: [_jsx("dt", { children: "Case ID" }), _jsx("dd", { className: "mono", children: r.referId != null ? r.referId : _jsx("span", { className: "text-muted", children: "\u2014" }) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Incident type" }), _jsx("dd", { children: r.issueType ? (_jsx("span", { title: r.issueType, children: r.issueType })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Level" }), _jsxs("dd", { children: [incidentLevelLabel(r.level), r.parentId != null ? ` · Parent L1 #${r.parentId}` : ''] })] }), _jsxs("div", { children: [_jsx("dt", { children: "Created by" }), _jsx("dd", { children: r.createdByName ? (_jsx("span", { title: r.createdByEmail, children: r.createdByName })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Owner" }), _jsx("dd", { children: r.ownerName ? r.ownerName : _jsx("span", { className: "text-muted", children: "\u2014" }) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Created" }), _jsx("dd", { children: formatDate(r.created) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Last Update" }), _jsx("dd", { children: formatDateTime(r.updated) })] })] })] }, r.id))) }, group[0].id))) })] })] }));
}
